import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_TOKEN_KEY = 'divine7777_admin_token';

export const getToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(ADMIN_TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ===== Auth =====
export const adminLogin = async (email, password) => {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  if (data.token) setToken(data.token);
  return data;
};

export const adminMe = async () => {
  const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
  return data;
};

export const adminLogout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() });
  } catch (err) {
    console.error('Admin logout request failed:', err);
  }
  clearToken();
};

// ===== Public =====
export const fetchBlogs = async () => (await axios.get(`${API}/blog`)).data;
export const fetchBlog = async (id) => (await axios.get(`${API}/blog/${id}`)).data;
export const fetchVideos = async () => (await axios.get(`${API}/videos`)).data;
export const fetchCrystals = async () => (await axios.get(`${API}/crystals`)).data;
export const fetchTestimonials = async () => (await axios.get(`${API}/testimonials`)).data;
export const fetchMusic = async () => (await axios.get(`${API}/music`)).data;
export const fetchActiveMusic = async () => (await axios.get(`${API}/music/active`)).data;
export const fetchSettings = async () => (await axios.get(`${API}/settings`)).data;

// ===== Admin: Blog =====
export const createBlog = async (payload) =>
  (await axios.post(`${API}/admin/blog`, payload, { headers: authHeaders() })).data;
export const updateBlog = async (id, payload) =>
  (await axios.put(`${API}/admin/blog/${id}`, payload, { headers: authHeaders() })).data;
export const deleteBlog = async (id) =>
  (await axios.delete(`${API}/admin/blog/${id}`, { headers: authHeaders() })).data;

// ===== Admin: Videos =====
export const createVideo = async (payload) =>
  (await axios.post(`${API}/admin/videos`, payload, { headers: authHeaders() })).data;
export const updateVideo = async (id, payload) =>
  (await axios.put(`${API}/admin/videos/${id}`, payload, { headers: authHeaders() })).data;
export const deleteVideo = async (id) =>
  (await axios.delete(`${API}/admin/videos/${id}`, { headers: authHeaders() })).data;

// ===== Admin: Crystals =====
export const createCrystal = async (payload) =>
  (await axios.post(`${API}/admin/crystals`, payload, { headers: authHeaders() })).data;
export const updateCrystal = async (id, payload) =>
  (await axios.put(`${API}/admin/crystals/${id}`, payload, { headers: authHeaders() })).data;
export const deleteCrystal = async (id) =>
  (await axios.delete(`${API}/admin/crystals/${id}`, { headers: authHeaders() })).data;

// ===== Admin: Testimonials =====
export const createTestimonial = async (payload) =>
  (await axios.post(`${API}/admin/testimonials`, payload, { headers: authHeaders() })).data;
export const updateTestimonial = async (id, payload) =>
  (await axios.put(`${API}/admin/testimonials/${id}`, payload, { headers: authHeaders() })).data;
export const deleteTestimonial = async (id) =>
  (await axios.delete(`${API}/admin/testimonials/${id}`, { headers: authHeaders() })).data;

// ===== Admin: Music =====
export const uploadMusic = async (file, title, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  const { data } = await axios.post(`${API}/admin/music/upload`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return data;
};
export const activateMusic = async (id) =>
  (await axios.put(`${API}/admin/music/${id}/activate`, {}, { headers: authHeaders() })).data;
export const renameMusic = async (id, title) =>
  (await axios.put(`${API}/admin/music/${id}`, { title }, { headers: authHeaders() })).data;
export const deleteMusic = async (id) =>
  (await axios.delete(`${API}/admin/music/${id}`, { headers: authHeaders() })).data;

// ===== Admin: Logo =====
export const uploadLogo = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post(`${API}/admin/logo/upload`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return data;
};
export const removeLogo = async () =>
  (await axios.delete(`${API}/admin/logo`, { headers: authHeaders() })).data;

// ===== Image upload =====
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post(`${API}/admin/upload`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  // Return full URL for use in img src
  return {
    ...data,
    fullUrl: `${BACKEND_URL}${data.url}`,
  };
};

export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // Frontend-public paths like /mystical-garden/... or /images/... are served by React
  return url;
};

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(' ');
  return JSON.stringify(detail);
};
